"use client";
import React, { useState, lazy, Suspense, useContext, useEffect } from "react";
import { FaStar, FaRegStar, FaHeart, FaShare, FaArrowRight } from "react-icons/fa";
import { motion } from "framer-motion";
import { ProductContext } from "../../context/ProductContext";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";
import { AuthContext } from "../../context/AuthContext";

// Lazy load product image
const LazyProductImage = lazy(() =>
  Promise.resolve({
    default: ({ src, alt, className }) => (
      <img src={src} alt={alt} className={className} loading="lazy" />
    ),
  })
);

// Star Rating Component
const StarRating = ({ rating, totalStars = 5 }) => {
  const fullStars = Math.floor(rating);
  return (
    <div className="flex items-center gap-1">
      {[...Array(totalStars)].map((_, index) => (
        <span key={index} className="text-yellow-400">
          {index < fullStars ? <FaStar size={14} /> : <FaRegStar size={14} />}
        </span>
      ))}
    </div>
  );
};

const ProductInfo = () => {
  const { product, loading } = useContext(ProductContext);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  const { user } = useContext(AuthContext);

  // Check subscription: either from context or localStorage
  const isSubscribed =
    !!user?.subscription?.id || !!localStorage.getItem("sub_id");

  const { wishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const [isInWishlist, setIsInWishlist] = useState(false);

  // Check if product is in wishlist
  useEffect(() => {
    if (product) {
      setIsInWishlist(wishlist.some((item) => item.id === product.id));
    }
  }, [wishlist, product]);

  if (loading) {
    return (
      <p className="text-center py-10 text-gray-500 text-lg">
        Loading product...
      </p>
    );
  }

  if (!product) {
    return (
      <p className="text-center py-10 text-red-500 text-lg">
        Product not found!
      </p>
    );
  }

  const handleQuantityChange = (change) => {
    setQuantity((prev) => Math.max(1, prev + change));
  };

const handleWishlistToggle = () => {
  if (isInWishlist) {
    removeFromWishlist(product.id);
  } else {
    addToWishlist({
      id: product.id,
      title: product.name,   // ✅ MAP name → title
      image: product.image,
    });
  }
  setIsInWishlist(!isInWishlist);
};

  return (
    <motion.section
      className="mt-12 max-md:mt-10 max-md:max-w-full"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-md:gap-6">
        {/* Image */}
        <motion.div
          className="w-full"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex flex-col justify-center items-center px-8 py-12 h-full min-h-[500px] bg-white rounded-xl shadow-[7px_7px_18px_rgba(0,0,0,0.1)] max-md:px-5 max-md:py-8 max-md:min-h-[400px]">
            <Suspense
              fallback={
                <div className="w-full h-64 bg-gray-200 animate-pulse rounded-lg flex items-center justify-center">
                  <span className="text-gray-500">Loading...</span>
                </div>
              }
            >
              <LazyProductImage
                src={product.image}
                alt={product.name}
                className="object-contain w-full max-w-sm h-auto aspect-[0.95] hover:scale-105 transition-transform duration-300"
              />
            </Suspense>
          </div>
        </motion.div>

        {/* Details */}
        <motion.div
          className="w-full"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex flex-col justify-center h-full min-h-[500px] max-md:min-h-auto pl-0 lg:pl-8 xl:pl-12">
            <div className="space-y-4">
              <span className="inline-block px-3 py-1 text-sm font-medium text-indigo-400 bg-indigo-50 rounded-full">
                {product.category_name}
              </span>

              <h2 className="text-3xl  leading-tight text-sky-950 max-md:text-2xl">
                {product.name}
              </h2>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <StarRating rating={4.2} />
                  <span className="text-sm font-semibold text-sky-950">4.2</span>
                </div>
                <span className="text-sm text-orange-600">(365 Reviews)</span>
              </div>

              <p className="text-base leading-relaxed text-neutral-600 mb-6">
                {product.content}
              </p>

              {/* Action Buttons */}
              <div className="space-y-4">
                <div className="flex items-center gap-4 max-md:flex-col max-md:gap-3">
                  {isSubscribed && (
                  <div className="flex items-stretch gap-0 bg-rose-400 rounded-full overflow-hidden flex-1 max-w-sm">
                    <button
                      onClick={() => handleQuantityChange(-1)}
                      className="px-4 py-3 text-white font-bold text-lg hover:bg-rose-500 transition-colors flex-shrink-0"
                    >
                      -
                    </button>
                    <div className="px-4 py-3 text-white font-bold text-lg flex items-center justify-center min-w-[3rem]">
                      {quantity}
                    </div>
                    <button
                      onClick={() => handleQuantityChange(1)}
                      className="px-4 py-3 text-white font-bold text-lg hover:bg-rose-500 transition-colors flex-shrink-0"
                    >
                      +
                    </button>
                    <button
                      onClick={() => {addToCart(product, quantity);
                        setQuantity(1); // ✅ Reset quantity to 1
                      }}
                      className="px-6 py-3 bg-sky-950 text-white font-medium hover:bg-sky-900 transition-colors flex-1 flex items-center justify-center gap-2"
                    >
                      Add To Box
                      <FaArrowRight size={14} />
                    </button>
                  </div>
                  )}
                  {/* Wishlist & Share */}
                  {isSubscribed && (
                  <div className="flex items-center gap-3 max-md:justify-center">
                    <motion.button
                      onClick={handleWishlistToggle}
                      className={`flex items-center justify-center w-12 h-12 rounded-full text-white transition-colors ${
                        isInWishlist ? "bg-red-500 hover:bg-red-600" : "bg-green-500 hover:bg-green-600"
                      }`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <FaHeart size={18} />
                    </motion.button>
                  </div>
                  )}
<div className="flex items-center gap-3 max-md:justify-center">
                   <motion.button
                      onClick={() => {
                        if (navigator.share) {
                          navigator
                            .share({
                              title: product.name,
                              text: `Check out this amazing product: ${product.name}!`,
                              url: window.location.href, // current page URL
                            })
                            .then(() => console.log("Shared successfully"))
                            .catch((error) => console.error("Share failed:", error));
                        } else {
                          // Fallback for unsupported browsers
                          navigator.clipboard
                            .writeText(window.location.href)
                            .then(() => alert("Link copied to clipboard!"))
                            .catch(() => alert("Could not copy link"));
                        }
                      }}
                      className="flex items-center justify-center w-12 h-12 bg-green-500 rounded-full text-white hover:bg-green-600 transition-colors"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <FaShare size={16} />
                    </motion.button>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default ProductInfo;
